import React, { useState, useCallback } from 'react'
import { IonItemGroup, IonItemDivider } from '@ionic/react'
import { groupBy, prop } from 'ramda'

import AssetItem from 'ui/components/items/AssetItem'
import SubmissionItemList from 'ui/components/lists/SubmissionItemList'
import ItemSkeleton from 'ui/components/loaders/ItemSkeleton'
import Typography, { Variant } from 'ui/components/typography/Typography'
import ConfirmationAlert from 'ui/components/alerts/ConfirmationAlert'

import { useCurrentLocale } from 'context/LocaleContext'
import useTranslation from 'hooks/useTranslation'
import useModel from 'hooks/useModel'
import useAssetLocalSubmissions from 'hooks/assets/useAssetLocalSubmissions'
import { useInspectionModels } from 'hooks/useModelList'
import useInterventionModels from 'hooks/useInterventionModels'

import { extractTitle, getTitle } from 'core/ContentModel'
import { Submission, SubmissionType } from 'core/Submission'
import formatDate from 'common/formatDate'

import getAssetRemoveAlertDescription from './getAssetRemoveAlertDescription'

interface LocalAssetItemProps {
  submission: Submission
  onClick: () => void
  onRemove: (assetId: string) => void
}

const LocalAssetItem: React.FC<LocalAssetItemProps> = (props) => {
  const { submission, onClick, onRemove } = props

  const t = useTranslation()
  const locale = useCurrentLocale()

  // model
  const { data: model, isLoading: isLoadingModel } = useModel(
    submission.modelId,
  )

  // submissions
  const relatedSubmissions = useAssetLocalSubmissions(submission.id)

  // inspection models
  const { data: inspectionModels = [], isLoading: isLoadingInspectionModels } =
    useInspectionModels(submission.modelId)

  // intervention models
  const {
    data: interventionModels = [],
    isLoading: isLoadingInterventionModels,
  } = useInterventionModels(submission.modelId)

  // removal
  const [showRemoveConfirmation, setRemoveConfirmation] = useState(false)

  // TODO ask for confirmation if a revision exists, too
  const handleRemove = useCallback(
    (submissionId: string) => {
      if (relatedSubmissions.length > 0) {
        setRemoveConfirmation(true)
      } else {
        onRemove(submissionId)
      }
    },
    [relatedSubmissions, onRemove],
  )

  if (
    isLoadingModel ||
    isLoadingInspectionModels ||
    isLoadingInterventionModels
  ) {
    return <ItemSkeleton />
  }

  // NOTE we may have an asset with no model, due to changes in permissions or
  // by using a shared phone
  // NOTE we are already preemptively returning if model is loading (see above)
  if (!model) return null

  // misc
  const assetTitle = extractTitle(model, submission)
  const title = assetTitle ?? getTitle(locale, model)

  const submissionsByType = groupBy(prop('type'), relatedSubmissions)

  const removeAlertDescription = getAssetRemoveAlertDescription(
    submissionsByType,
    t,
  )

  return (
    <>
      <ConfirmationAlert
        isOpen={showRemoveConfirmation}
        title={t('confirmation.deleteElement')}
        description={removeAlertDescription}
        acceptText={t('buttons.confirmSelection')}
        cancelText={t('buttons.cancelSelection')}
        onAccept={() => onRemove(submission.id)}
        onDidDismiss={() => setRemoveConfirmation(false)}
      />
      <AssetItem
        id={submission.id}
        title={title}
        details={formatDate(submission.lastUpdatedAt)}
        status={submission.status}
        onClick={onClick}
        onDelete={() => handleRemove(submission.id)}
      >
        {(submissionsByType[SubmissionType.NewInspection] ?? []).length > 0 ? (
          <IonItemGroup>
            <IonItemDivider color="light">
              <Typography
                variant={Variant.overline}
                style={{ fontWeight: 'bold' }}
              >
                {t('inspection')}
              </Typography>
            </IonItemDivider>
            <SubmissionItemList
              assetTitle={assetTitle}
              models={inspectionModels}
              submissions={submissionsByType[SubmissionType.NewInspection]}
            />
          </IonItemGroup>
        ) : null}
        {(submissionsByType[SubmissionType.NewIntervention] ?? []).length >
        0 ? (
          <IonItemGroup>
            <IonItemDivider color="light">
              <Typography
                variant={Variant.overline}
                style={{ fontWeight: 'bold' }}
              >
                {t('intervention')}
              </Typography>
            </IonItemDivider>
            <SubmissionItemList
              assetTitle={assetTitle}
              models={interventionModels}
              submissions={submissionsByType[SubmissionType.NewIntervention]}
            />
          </IonItemGroup>
        ) : null}
      </AssetItem>
    </>
  )
}

export default LocalAssetItem
